import React, { useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { statusTone, StatusTone } from '../../theme';

export type AuthStatusTone = 'success' | 'error' | 'warning' | 'info' | 'neutral';

const TONE_PALETTE: Record<AuthStatusTone, StatusTone> = {
  success: 'done',
  error: 'problem',
  warning: 'attention',
  info: 'active',
  neutral: 'neutral',
};

const DEFAULT_ICONS: Record<AuthStatusTone, React.ReactNode> = {
  success: <CheckCircleOutlinedIcon />,
  error: <ErrorOutlineOutlinedIcon />,
  warning: <WarningAmberOutlinedIcon />,
  info: <InfoOutlinedIcon />,
  neutral: <InfoOutlinedIcon />,
};

/** Shared size for the one heading on every sign-in and onboarding screen. */
export const AUTH_TITLE_VARIANT = 'h3' as const;

export interface AuthStatusAction {
  label: string;
  /** A route inside the app: renders a link. */
  to?: string;
  /** An external or mailto address: renders a link. */
  href?: string;
  onClick?: () => void;
  loading?: boolean;
  /** Shown instead of the label while `loading`, e.g. "Sending…". */
  loadingLabel?: string;
  disabled?: boolean;
}

interface AuthStatusProps {
  tone: AuthStatusTone;
  /** An outlined icon; each tone has a default. */
  icon?: React.ReactNode;
  title: string;
  /** One line of text: what happened and what to do next. */
  description?: React.ReactNode;
  primaryAction?: AuthStatusAction;
  /** A quieter text action under the primary button. */
  secondaryAction?: AuthStatusAction;
  /** Inline feedback (an Alert) shown between the text and the actions. */
  children?: React.ReactNode;
  /** A last, quiet link under the actions, e.g. "Back to sign in". */
  footer?: React.ReactNode;
  /**
   * Move focus to the title when the block appears. For states that replace
   * a form after an action, so keyboard and screen-reader users land on
   * the result instead of on nothing.
   */
  focusOnMount?: boolean;
}

const ActionButton: React.FC<{ action: AuthStatusAction; variant: 'contained' | 'text' }> = ({ action, variant }) => {
  const primary = variant === 'contained';
  const content = action.loading ? (
    <>
      <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} aria-hidden="true" />
      {action.loadingLabel ?? action.label}
    </>
  ) : (
    action.label
  );
  const common = {
    variant,
    fullWidth: primary,
    size: primary ? ('large' as const) : ('medium' as const),
    disabled: Boolean(action.disabled || action.loading),
  };

  if (action.to) {
    return (
      <Button component={RouterLink} to={action.to} onClick={action.onClick} {...common}>
        {content}
      </Button>
    );
  }
  if (action.href) {
    return (
      <Button href={action.href} onClick={action.onClick} {...common}>
        {content}
      </Button>
    );
  }
  return (
    <Button onClick={action.onClick} {...common}>
      {content}
    </Button>
  );
};

/**
 * The one way the sign-in and onboarding screens show a result: a link that
 * was sent, a link that has expired, an account that was created. A tinted
 * icon tile, a sentence-case title, one line of text and a way forward, so
 * no state is a dead end.
 */
const AuthStatus: React.FC<AuthStatusProps> = ({
  tone,
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  children,
  footer,
  focusOnMount = false,
}) => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const palette = statusTone[TONE_PALETTE[tone]];

  useEffect(() => {
    if (focusOnMount) titleRef.current?.focus();
  }, [focusOnMount]);

  return (
    <Box data-testid="auth-status" data-tone={tone}>
      <Box
        aria-hidden="true"
        sx={{
          width: 48,
          height: 48,
          borderRadius: 3,
          display: 'grid',
          placeItems: 'center',
          backgroundColor: palette.bg,
          color: palette.fg,
          mb: 3,
          '& svg': { fontSize: 24 },
        }}
      >
        {icon ?? DEFAULT_ICONS[tone]}
      </Box>

      <Typography
        ref={titleRef}
        component="h1"
        variant={AUTH_TITLE_VARIANT}
        tabIndex={-1}
        sx={{ mb: 1, '&:focus': { outline: 'none' } }}
      >
        {title}
      </Typography>

      {description && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, overflowWrap: 'anywhere' }}>
          {description}
        </Typography>
      )}

      {children && <Box sx={{ mb: 3 }}>{children}</Box>}

      {primaryAction && <ActionButton action={primaryAction} variant="contained" />}

      {secondaryAction && (
        <Box sx={{ mt: 1.5, textAlign: 'center' }}>
          <ActionButton action={secondaryAction} variant="text" />
        </Box>
      )}

      {footer && <Box sx={{ mt: 3, textAlign: 'center' }}>{footer}</Box>}
    </Box>
  );
};

export default AuthStatus;
